<template>
	<fragment v-if="isUserAllowedInLevel2">
		<BaseHeader :title="$t('overview_open_positions').toString()" />

		<div v-if="damageCases && statistics" class="flex justify-between">
			<BaseCard :title="$t('all_cases')" class="m-4 w-1/4" :is-toggleable="false">
				<div class="flex justify-between">
					<div>{{ $t('total_cases') }}</div>
					<div class="font-bold ">{{ statistics.countDamageCases }}</div>
				</div>

				<div class="flex justify-between">
					<div>{{ $t('closed_cases') }}</div>
					<div class="font-bold ">{{ statistics.countClosedDamageCases }}</div>
				</div>

				<div class="flex justify-between">
					<div>{{ $t('open_cases') }}</div>
					<div class="font-bold ">{{ statistics.countOpenDamageCases }}</div>
				</div>
			</BaseCard>

			<BaseCard :title="$t('all_finances')" class="m-4 w-1/4" :is-toggleable="false">
				<div class="flex justify-between">
					<div>{{ $t('total_gross') }}</div>
					<div class="font-bold ">{{ formatMoney(statistics.sumRechnungsbetrag) }}</div>
				</div>

				<div class="flex justify-between">
					<div>{{ $t('open_positions') }}</div>
					<div class="font-bold ">{{ formatMoney(statistics.sumOffenerBetrag) }}</div>
				</div>
			</BaseCard>

			<BaseCard :title="$t('shown_cases')" class="m-4 w-1/4" :is-toggleable="false">
				<div class="flex justify-between">
					<div>{{ $t('total_cases') }}</div>
					<div class="font-bold ">{{ damageCases.length }}</div>
				</div>

				<div class="flex justify-between">
					<div>{{ $t('closed_cases') }}</div>
					<div class="font-bold ">{{ closedDamageCases(damageCases).length }}</div>
				</div>

				<div class="flex justify-between">
					<div>{{ $t('open_cases') }}</div>
					<div class="font-bold ">{{ openDamageCases(damageCases).length }}</div>
				</div>
			</BaseCard>

			<BaseCard :title="$t('shown_finances')" class="m-4 w-1/4" :is-toggleable="false">
				<div class="flex justify-between">
					<div>{{ $t('total_gross') }}</div>
					<div class="font-bold ">{{ bruttoDamageCases(damageCases) }}</div>
				</div>

				<div class="flex justify-between">
					<div>{{ $t('open_cases') }}</div>
					<div class="font-bold ">{{ offenePostenDamageCases(damageCases) }}</div>
				</div>
			</BaseCard>
		</div>

		<OverviewSearch
			ref="overviewSearch"
			@search="search"
			@directSearch="directSearch"
			:is-search-running="isSearchRunning"
			:is-for-payment="true"
			:show-col="showCol"
			class="mb-8"
		>
		</OverviewSearch>

		<div v-if="damageCases">
			<div class="flex justify-between align-middle mb-4 ml-4">
				<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="mr-4 self-center" />
				<div class="flex">
					<PaginationSizeSelect @input="directSearch" v-model="per_page" class="mr-4 w-40" v-show="damageCases && damageCases.length" />
					<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" class="mr-4" />
				</div>
			</div>
			<div class="flex flex-col">
				<div>
					<div class="align-middle inline-block min-w-full shadow overflow-hidden border-b border-gray-200">
						<div class="m-4" v-if="this.damageCases && this.damageCases.length <= 0">
							<b>{{ $t('no_entries_found') }}</b>
						</div>
						<table class="min-w-full" v-if="this.damageCases && this.damageCases.length > 0">
							<thead>
								<tr>
									<OverviewTableHeader
										:current-sort="currentSortField === 'date_of_accident'"
										:current-sort-dir="currentSortDirection"
										:label="$t('billing_date').toString()"
										@click.native="sortby('damagecasePaymentDate')"
									/>
									<OverviewTableHeader
										:current-sort="currentSortField === 'licensePlate'"
										:current-sort-dir="currentSortDirection"
										:label="$t('license_plate').toString()"
										@click.native="sortby('licensePlate')"
									/>

									<OverviewTableHeader
										:current-sort="currentSortField === 'openDays'"
										:current-sort-dir="currentSortDirection"
										:label="$t('open_days').toString()"
										@click.native="sortby('openDays')"
									/>

									<OverviewTableHeader
										:current-sort="currentSortField === 'customer'"
										:current-sort-dir="currentSortDirection"
										:label="$t('owner_name').toString()"
										@click.native="sortby('customer')"
									/>

									<OverviewTableHeader
										:current-sort="currentSortField === 'payment'"
										:current-sort-dir="currentSortDirection"
										:label="$t('amount_slash_open').toString()"
										@click.native="sortby('payment')"
									/>

									<OverviewTableHeader
										:current-sort="currentSortField === 'imd_net'"
										:current-sort-dir="currentSortDirection"
										:label="$t('imd_net_id').toString()"
										@click.native="sortby('imd_net')"
									/>

									<OverviewTableHeader
										:current-sort="currentSortField === 'deadline'"
										:current-sort-dir="currentSortDirection"
										:label="$t('deadline_until').toString()"
										@click.native="sortby('deadline')"
									/>

									<OverviewTableHeader
										:current-sort="currentSortField === 'zahlungsziel'"
										:current-sort-dir="currentSortDirection"
										:label="$t('payment_due').toString()"
										@click.native="sortby('zahlungsziel')"
									/>

									<OverviewTableHeader label="Status" :cursor-pointer="false" />
								</tr>
							</thead>
							<tbody class="bg-white">
								<tr
									v-for="damageCase in damageCases"
									:key="damageCase.id"
									class="tr odd:bg-gray-200 cursor-pointer"
									@click="$router.push({ name: 'damagecases.payment', params: { processId: damageCase.id, customer: damageCase.customer.id } })"
								>
									<td class="paymentCell">
										{{ formatDate(damageCase.paymentDamageAssessor.payment.date) }}
									</td>
									<td class="paymentCell">
										{{ damageCase.vehicle.licensePlate.text }}
									</td>

									<td class="paymentCell">
										{{ damageCase.paymentDamageAssessor.open_days }}
									</td>

									<td class="paymentCell">
										<span v-if="damageCase.customer.companyName != null" class="hidden lg:inline-block">{{ damageCase.customer.companyName }}</span>
										<span v-else class="hidden lg:inline-block">{{
											damageCase.customer.companyName != null ? damageCase.customer.companyName : damageCase.customer.firstName
										}}</span>
										{{ damageCase.customer.lastName }}
									</td>

									<td class="paymentCell">
										{{ formatMoney(damageCase.paymentDamageAssessor.rechnungsbetrag) }} /
										{{ formatMoney(damageCase.paymentDamageAssessor.offener_betrag) }}
									</td>

									<td class="paymentCell">
										{{ damageCase.imdNetId }}
									</td>

									<td class="paymentCell" :class="isDateRed(damageCase.paymentDamageAssessor.deadline) ? 'text-red-700 font-bold' : ''">
										{{ formatDate(damageCase.paymentDamageAssessor.deadline) }}
									</td>

									<td class="paymentCell" :class="isDateRed(damageCase.paymentDamageAssessor.zahlungsziel) ? 'text-red-700 font-bold' : ''">
										{{ formatDate(damageCase.paymentDamageAssessor.zahlungsziel) }}
									</td>

									<td
										class="paymentCell px-6 py-4 whitespace-no-wrap"
										@click.stop="
											$router.push({ name: 'damagecases.dunning', params: { processId: damageCase.id, customer: damageCase.customer.id } })
										"
									>
										{{ getStatusLabel(damageCase.paymentDamageAssessor.payment.status, damageCase.lawyerOffice.name) }}
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>

		<div class="flex justify-between align-middle mb-4 ml-4 mt-4">
			<PaginationNumberOfPages :current-page="current_page" :last-page="last_page" :total="total" class="mr-4 self-center" />
			<div class="flex">
				<PaginationSizeSelect @input="directSearch" v-model="per_page" class="mr-4 w-40" v-show="damageCases && damageCases.length" />
				<BasePagination v-model="current_page" :last-page="last_page" :total="total" :is-blocked="blockSearch" class="mr-4" />
			</div>
		</div>
	</fragment>
	<Forbidden v-else />
</template>

<script>
import { Fragment } from 'vue-fragment';
import { DamagecasesApiFactory, StatisticsApiFactory } from '@/api/generated';
import { getStringFromDate, getUnixTimestamp } from '@/plugins/Utils';
import apiDefaults from '@/api/defaultConfiguration';
import BaseHeader from '@/components/navigation/BaseHeader';
import OverviewSearch from '@/components/damagecases/forms/OverviewSearch';
import OverviewTableHeader from '@/views/damagecases/OverviewTableHeader';
import BasePagination from '@/components/forms/buttons/BasePagination';
import PaginationSizeSelect from '@/components/damagecases/forms/PaginationSizeSelect';
import PaginationNumberOfPages from '@/components/forms/buttons/PaginationNumberOfPages';
import Formatter from '@/mixins/Formatter';
import DunningStatus from '@/mixins/damagecase/DunningStatus';
import BaseCard from '@/components/damagecases/forms/BaseCard';
import defaultConfiguration from '@/api/defaultConfiguration';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden.vue';

export default {
	name: 'PaymentOverview',
	components: {
		Forbidden,
		BaseCard,
		PaginationNumberOfPages,
		PaginationSizeSelect,
		BasePagination,
		OverviewTableHeader,
		OverviewSearch,
		BaseHeader,
		Fragment,
	},
	mixins: [Formatter, DunningStatus, Authentication],
	data() {
		return {
			per_page: { display: '25', value: 25 },
			damageCases: [],
			searchQuery: undefined,
			timeoutIndex: 0,
			isSearchRunning: false,
			currentSortField: 'date_of_accident',
			currentSortDirection: 'asc',
			current_page: 1,
			last_page: 1,
			total: 0,
			blockSearch: false,
			blockPageReset: false,
			statistics: {
				countDamageCases: 0,
				countClosedDamageCases: 0,
				countOpenDamageCases: 0,
				sumRechnungsbetrag: 0,
				sumOffenerBetrag: 0,
			},
		};
	},
	created() {
		if (this.isUserAllowedInLevel2) {
			StatisticsApiFactory(defaultConfiguration.config(), defaultConfiguration.basePath, defaultConfiguration.axios)
				.damagecasesStatisticsGet()
				.then(response => {
					if (response.data) {
						this.statistics = response.data;
					}
				});
		}
	},
	mounted() {
		this.$emit('scrollUp', this.pageIndex);
		this.$refs['overviewSearch'].directSearch();
	},
	watch: {
		current_page: function(oldValue, newValue) {
			if (oldValue !== newValue) {
				this.blockPageReset = true;
				this.$refs['overviewSearch'].directSearch();
			}
		},
	},
	methods: {
		isDateRed(dateToCheck) {
			return getUnixTimestamp() > dateToCheck;
		},
		getStatusLabel(paymentStatus) {
			if (paymentStatus === 'im_mahnverfahren') return this.$t('dunning_process_by_lawyers_office');
			else return this.makeStatusLabel(paymentStatus);
		},
		closedDamageCases(damageCases) {
			return damageCases.filter(dc => {
				return dc.isClosed;
			});
		},
		openDamageCases(damageCases) {
			return damageCases.filter(dc => {
				return !dc.isClosed;
			});
		},
		bruttoDamageCases(damageCases) {
			let brutto = 0;
			damageCases.map(dc => {
				brutto += dc.rechnungsbetrag;
			});
			return this.formatMoney(brutto);
		},
		offenePostenDamageCases(damageCases) {
			let value = 0;
			damageCases.map(dc => {
				value += dc.offener_betrag < 0 ? 0 : dc.offener_betrag;
			});
			return this.formatMoney(value);
		},
		formatedMoney(money) {
			if (!money) {
				return 0;
			}
			return new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, style: 'currency', currency: 'EUR' }).format(money);
		},
		getStringFromDate(date) {
			getStringFromDate(new Date(date * 1000));
		},
		search($event) {
			clearTimeout(this.timeoutIndex);
			this.timeoutIndex = setTimeout(() => {
				this.directSearch($event);
			}, 300);
		},
		directSearch($event) {
			if (this.blockSearch) {
				return;
			}
			if (!this.blockPageReset) {
				this.current_page = 1;
			}

			this.blockSearch = true;
			this.isSearchRunning = true;
			let sort = '';

			if (this.currentSortField === 'date_of_accident') {
				sort = (this.currentSortDirection === 'desc' ? '' : '-') + this.currentSortField;
			} else {
				sort = (this.currentSortDirection === 'desc' ? '-' : '') + this.currentSortField;
			}

			DamagecasesApiFactory(apiDefaults.config(), apiDefaults.basePath, apiDefaults.axios)
				.damagecasesGet(
					this.current_page,
					this.per_page ? this.per_page.value : undefined,
					sort,
					$event.range ? $event.range : undefined,
					$event.year ? $event.year : undefined,
					$event.status ? $event.status : undefined,
					$event.damageAssessorReferenceNumber ? $event.damageAssessorReferenceNumber : undefined,
					$event.lawyerOfficeReferenceNumber ? $event.lawyerOfficeReferenceNumber : undefined,
					$event.repairShopReferenceNumber ? $event.repairShopReferenceNumber : undefined,
					$event.vehicles ? $event.vehicles : undefined,
					$event.imd_net ? $event.imd_net : undefined,
					$event.customer ? $event.customer : undefined,
					true,
					undefined,
					undefined,
					undefined,
					(this.isUserAllowedInLevel5or6 || this.isUserAllowedInLevel2) && this.isAuthenticatedUserInGroupMitarbeiter && $event.status === 'opened',
				)
				.then(response => {
					this.damageCases = response.data.data;
					this.current_page = response.data.meta.current_page;
					this.last_page = response.data.meta.last_page;
					this.total = response.data.meta.total;
					this.isSearchRunning = false;
					this.blockSearch = false;
					this.blockPageReset = false;
				})
				.catch(error => {
					this.isSearchRunning = false;
				});
		},
		showCol: function(showLvl) {
			return showLvl.indexOf(parseInt(this.$store.getters.getAuthenticatedUserGroupLevel)) !== -1;
		},
		sortby: function(field) {
			if (field === this.currentSortField) {
				this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc';
			} else {
				this.currentSortDirection = 'asc';
			}
			this.currentSortField = field;
			this.$refs['overviewSearch'].directSearch();
		},
		formatDate: function(timestamp) {
			if (!timestamp) {
				return '';
			}
			const date = new Date(timestamp * 1000);
			return getStringFromDate(date);
		},
	},
};
</script>

<style scoped>
.paymentCell {
	@apply border-b border-gray-200 break-words;
}
</style>
